'use client';

import { useEffect, useRef } from 'react';

/**
 * StickyNavbar - React component that enables sticky behavior of navigation bar on page scroll
 * @param {Object} props - Component props
 * @param {number} props.scrollOffset - Scroll position at which the navbar becomes sticky (default: 500)
 * @param {string} props.className - Additional class names
 * @param {React.ReactNode} props.children - Child elements
 */
const StickyNavbar = ({ 
  scrollOffset = 500,
  className = '',
  children,
  ...props
}) => {
  const navbarRef = useRef(null);

  useEffect(() => {
    const navbar = navbarRef.current;
    if (!navbar) return;

    const navbarHeight = navbar.offsetHeight;
    const isAbsolute = navbar.classList.contains('position-absolute');

    const handleScroll = () => {
      if (window.pageYOffset > scrollOffset) {
        navbar.classList.add('navbar-stuck');
        
        // If navbar is not position-absolute, add padding to body
        if (!isAbsolute) {
          document.body.style.paddingTop = `${navbarHeight}px`;
        }
      } else {
        navbar.classList.remove('navbar-stuck');
        
        // If navbar is not position-absolute, remove padding from body
        if (!isAbsolute) {
          document.body.style.paddingTop = '';
        }
      }
    };

    // Initial check
    handleScroll();
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollOffset]);

  return (
    <nav 
      ref={navbarRef}
      className={`navbar-sticky ${className}`}
      {...props}
    >
      {children}
    </nav>
  );
};

export default StickyNavbar; 